import type {
  OrgModelEntity,
  OrgModelProjection,
} from '../models/OrgModelProjection';
import type { PositionStaffingPersonRow } from '../models/PositionStaffingIntent';

export type PositionStaffingGroupBy =
  | 'status'
  | 'role'
  | 'level'
  | 'department'
  | 'experience';

export interface PositionStaffingCandidate {
  readonly row: PositionStaffingPersonRow;
  readonly person: OrgModelEntity | null;
  readonly currentPosition: OrgModelEntity | null;
  readonly role: string;
  readonly level: string;
  readonly department: string;
  readonly experience: string;
  readonly skills: readonly string[];
}

export interface PositionStaffingCandidateGroup {
  readonly id: string;
  readonly label: string;
  readonly candidates: readonly PositionStaffingCandidate[];
}

export interface PositionStaffingCorrelation {
  readonly matchedRequirements: readonly string[];
  readonly missingRequirements: readonly string[];
  readonly candidateSkills: readonly string[];
}

export interface PositionStaffingDestination {
  readonly positionId: string;
  readonly label: string;
  readonly primaryBlocked: boolean;
}

export function staffingCandidatePositionId(
  candidate: PositionStaffingCandidate,
): string | null {
  return candidate.currentPosition?.id ?? candidate.row.primaryPositionId ?? null;
}

const POSITION_LEVEL_FIELDS = ['level', 'jobLevel', 'positionLevel', 'grade', 'careerLevel'] as const;
const DEPARTMENT_FIELDS = ['department', 'departmentName', 'businessUnit', 'function'] as const;
const EXPERIENCE_FIELDS = ['experienceLevel', 'seniority', 'careerStage'] as const;
const EXPERIENCE_YEAR_FIELDS = ['yearsExperience', 'yearsOfExperience'] as const;

export function buildPositionStaffingCandidates(
  rows: readonly PositionStaffingPersonRow[],
  model: OrgModelProjection,
): PositionStaffingCandidate[] {
  const peopleById = new Map(model.people.map((person) => [person.id, person] as const));
  const positionsByRef = new Map<string, OrgModelEntity>();
  for (const position of model.positions) {
    positionsByRef.set(position.id, position);
    positionsByRef.set(`record:${position.id}`, position);
    if (position.externalId) {
      positionsByRef.set(position.externalId, position);
      positionsByRef.set(`record:${position.externalId}`, position);
    }
  }
  const primaryPositionByPersonId = new Map<string, string>();
  if (model.occupancyVisibility === 'visible') {
    for (const state of model.positionState) {
      if (state.occupancyVisibility !== 'visible') continue;
      const primaryPersonId = state.primaryOccupantPersonId
        ?? state.occupants.find((occupant) => occupant.assignmentType === 'primary')?.personId
        ?? null;
      if (primaryPersonId && !primaryPositionByPersonId.has(primaryPersonId)) {
        primaryPositionByPersonId.set(primaryPersonId, state.positionId);
      }
    }
  }
  const skillsByPerson = new Map<string, string[]>();

  for (const assertion of model.skillAssertions) {
    const values = skillsByPerson.get(assertion.actorRef) ?? [];
    if (!values.some((value) => normalize(value) === normalize(assertion.skillLabel))) {
      values.push(assertion.skillLabel);
      skillsByPerson.set(assertion.actorRef, values);
    }
  }

  return rows
    .map((row): PositionStaffingCandidate => {
      const person = peopleById.get(row.personId) ?? null;
      const currentPositionRef = row.primaryPositionId
        ?? primaryPositionByPersonId.get(row.personId)
        ?? null;
      const currentPosition = currentPositionRef
        ? positionsByRef.get(currentPositionRef) ?? null
        : null;
      const role = currentPosition?.label
        ?? textProperty(person, ['jobTitle', 'title', 'role'])
        ?? 'Unassigned';
      const level = textProperty(currentPosition, POSITION_LEVEL_FIELDS)
        ?? textProperty(person, POSITION_LEVEL_FIELDS)
        ?? 'Level not provided';
      const department = textProperty(currentPosition, DEPARTMENT_FIELDS)
        ?? textProperty(person, DEPARTMENT_FIELDS)
        ?? 'Department not provided';
      const experience = textProperty(person, EXPERIENCE_FIELDS)
        ?? experienceFromYears(numberProperty(person, EXPERIENCE_YEAR_FIELDS))
        ?? 'Experience not provided';

      return {
        row,
        person,
        currentPosition,
        role,
        level,
        department,
        experience,
        skills: [...(skillsByPerson.get(row.personId) ?? [])].sort(localeCompare),
      };
    })
    .sort((left, right) => localeCompare(left.row.displayLabel, right.row.displayLabel));
}

export function groupPositionStaffingCandidates(
  candidates: readonly PositionStaffingCandidate[],
  groupBy: PositionStaffingGroupBy,
): PositionStaffingCandidateGroup[] {
  const groups = new Map<string, PositionStaffingCandidate[]>();
  for (const candidate of candidates) {
    const label = groupLabel(candidate, groupBy);
    const values = groups.get(label) ?? [];
    values.push(candidate);
    groups.set(label, values);
  }

  return [...groups.entries()]
    .map(([label, values]) => ({
      id: `${groupBy}:${normalize(label) || 'unknown'}`,
      label,
      candidates: values.sort((left, right) =>
        localeCompare(left.row.displayLabel, right.row.displayLabel)),
    }))
    .sort((left, right) => {
      if (left.label === 'Unassigned') return -1;
      if (right.label === 'Unassigned') return 1;
      return localeCompare(left.label, right.label);
    });
}

export function correlateStaffingCandidate(
  candidate: PositionStaffingCandidate,
  targetPositionId: string | null,
  model: OrgModelProjection,
): PositionStaffingCorrelation {
  const candidateSkills = [...candidate.skills];
  if (!targetPositionId) {
    return {
      matchedRequirements: [],
      missingRequirements: [],
      candidateSkills,
    };
  }

  const requirements = model.capabilityRequirements
    .filter((requirement) =>
      requirement.targetRef === targetPositionId
      && requirement.kind === 'skill'
      && requirement.required)
    .map((requirement) => requirement.capabilityLabel);
  const knownSkills = new Set(candidateSkills.map(normalize));

  return {
    matchedRequirements: requirements.filter((requirement) => knownSkills.has(normalize(requirement))),
    missingRequirements: requirements.filter((requirement) => !knownSkills.has(normalize(requirement))),
    candidateSkills,
  };
}

export function buildPositionStaffingDestinations(
  model: OrgModelProjection,
  selectedPositionId: string | null,
): PositionStaffingDestination[] {
  const stateByPositionId = new Map(
    model.positionState.map((state) => [state.positionId, state]),
  );
  const destinations: PositionStaffingDestination[] = [];
  for (const position of model.positions) {
    if (position.id === selectedPositionId) continue;
    destinations.push({
      positionId: position.id,
      label: position.label,
      primaryBlocked: Boolean(
        stateByPositionId.get(position.id)?.primaryOccupantPersonId,
      ),
    });
  }
  return destinations;
}

function groupLabel(
  candidate: PositionStaffingCandidate,
  groupBy: PositionStaffingGroupBy,
): string {
  if (groupBy === 'role') return candidate.role;
  if (groupBy === 'level') return candidate.level;
  if (groupBy === 'department') return candidate.department;
  if (groupBy === 'experience') return candidate.experience;
  if (candidate.currentPosition) return 'Currently assigned';
  if (candidate.row.unassigned === true) return 'Unassigned';
  if (candidate.row.unassigned === false) return 'Currently assigned';
  return 'Assignment status hidden';
}

function textProperty(
  entity: OrgModelEntity | null,
  keys: readonly string[],
): string | null {
  if (!entity) return null;
  for (const key of keys) {
    const value = entity.props[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return null;
}

function numberProperty(
  entity: OrgModelEntity | null,
  keys: readonly string[],
): number | null {
  if (!entity) return null;
  for (const key of keys) {
    const value = entity.props[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

function experienceFromYears(years: number | null): string | null {
  if (years === null) return null;
  if (years < 2) return 'Early career';
  if (years < 5) return 'Developing';
  if (years < 10) return 'Experienced';
  return 'Highly experienced';
}

function normalize(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function localeCompare(left: string, right: string): number {
  return left.localeCompare(right, undefined, { sensitivity: 'base' });
}
